import LegalPage from "@/components/LegalPage";

export default function ShippingPolicy() {
  return (
    <LegalPage title="Shipping Policy" testId="shipping-policy-page">
      <p>True Impact Peptides</p>
      <p>Last Updated: August 2026</p>

      <p>Thank you for choosing True Impact Peptides. We are committed to processing and shipping orders efficiently while maintaining the quality, security, and integrity of our products.</p>
      <p>This Shipping Policy explains how orders are processed, shipped, and delivered within the United States.</p>
      <p>By placing an order through our Website, you acknowledge and agree to this Shipping Policy.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">1. Shipping Availability</h3>
      <p>At this time, True Impact Peptides ships only within the United States. We do not currently offer international shipping. If international shipping becomes available in the future, this policy will be updated accordingly.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">2. Order Processing</h3>
      <p>Orders are generally processed after:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Payment has been successfully authorized.</li>
        <li>Fraud screening has been completed.</li>
        <li>Inventory has been confirmed.</li>
        <li>The order has been approved for fulfillment.</li>
      </ul>
      <p>Most orders are processed within one to three (1-3) business days. Business days do not include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Saturdays</li>
        <li>Sundays</li>
        <li>Federal holidays</li>
      </ul>
      <p>During periods of high order volume, promotional events, severe weather, or other unforeseen circumstances, processing times may be longer.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">3. Shipping Time</h3>
      <p>Once an order has been processed and transferred to the shipping carrier, delivery time depends on the shipping method selected at checkout and the carrier's operations. Shipping estimates displayed during checkout are estimates only and are not guaranteed. Delivery times may vary based on:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Customer location</li>
        <li>Weather conditions</li>
        <li>Carrier delays</li>
        <li>Holidays</li>
        <li>Operational disruptions</li>
        <li>Other circumstances outside our control</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">4. Shipping Confirmation</h3>
      <p>When available, customers will receive a shipping confirmation email containing tracking information once the order has been prepared for shipment. Tracking information may take time to update after the shipping label has been created.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">5. Shipping Charges</h3>
      <p>Shipping charges are calculated during checkout based on factors such as:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Destination</li>
        <li>Shipping method</li>
        <li>Order size</li>
        <li>Carrier pricing</li>
      </ul>
      <p>Shipping costs are displayed before the customer completes checkout.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">6. Address Accuracy</h3>
      <p>Customers are responsible for providing a complete and accurate shipping address. Please carefully review your shipping information before submitting your order. True Impact Peptides is not responsible for delays, losses, or returned shipments resulting from:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Incorrect addresses</li>
        <li>Incomplete addresses</li>
        <li>Undeliverable addresses</li>
        <li>Customer entry errors</li>
      </ul>
      <p>If an order is returned because of an incorrect shipping address, additional shipping charges may apply before reshipment.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">7. Lost, Delayed, or Stolen Packages</h3>
      <p>Once an order has been transferred to the shipping carrier, delivery is subject to the carrier's operations. True Impact Peptides is not responsible for delays caused by:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Carrier issues</li>
        <li>Weather</li>
        <li>Natural disasters</li>
        <li>Labor disruptions</li>
        <li>Government actions</li>
        <li>Other events outside our reasonable control</li>
      </ul>
      <p>If a package appears to be lost, customers should contact both the shipping carrier and True Impact Peptides as soon as possible. While we will make reasonable efforts to assist in resolving shipping issues, we cannot guarantee replacement or reimbursement for packages confirmed as delivered by the carrier but subsequently lost or stolen.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">8. Damaged Shipments</h3>
      <p>Customers should inspect all shipments promptly upon delivery. If your package arrives visibly damaged or the contents appear damaged during transit, please contact us promptly at info@trueimpactpeptides.com. When possible, please include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Order number</li>
        <li>Photographs of the shipping package</li>
        <li>Photographs of the product</li>
        <li>Description of the damage</li>
      </ul>
      <p>We may request additional information while investigating the claim.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">9. Incorrect Shipments</h3>
      <p>If you receive the wrong product, missing items, or an incorrect quantity, please notify us promptly after delivery. Include:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Order number</li>
        <li>Description of the issue</li>
        <li>Photographs, if applicable</li>
      </ul>
      <p>We will review the issue and determine an appropriate resolution.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">10. Refused Deliveries</h3>
      <p>Orders refused by the customer without prior authorization may be subject to:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Return shipping charges</li>
        <li>Restocking costs (if applicable)</li>
        <li>Any additional expenses incurred by the Company</li>
      </ul>
      <p>Refund eligibility will be determined in accordance with our Returns &amp; Refund Policy.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">11. Shipping Restrictions</h3>
      <p>True Impact Peptides reserves the right to refuse shipment to any location where shipment would violate applicable laws or regulations. We also reserve the right to cancel or refuse any shipment if we reasonably believe:</p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Fraud is involved.</li>
        <li>The order violates our Terms &amp; Conditions.</li>
        <li>Regulatory concerns exist.</li>
        <li>The transaction presents an unacceptable business risk.</li>
      </ul>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">12. Risk of Loss</h3>
      <p>Ownership and risk of loss transfer to the customer upon delivery of the shipment to the shipping carrier. Customers assume responsibility for the shipment after it has been accepted by the carrier, subject to applicable law.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">13. Research Use Only Products</h3>
      <p>All products shipped by True Impact Peptides are intended solely for lawful laboratory research purposes. Nothing contained in this Shipping Policy modifies or limits the Research Use Only Disclaimer, Terms &amp; Conditions, or any other legal policy published on the Website.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">14. Policy Updates</h3>
      <p>True Impact Peptides reserves the right to modify this Shipping Policy at any time. Changes become effective upon posting to the Website. The "Last Updated" date at the top of this page indicates when revisions become effective.</p>

      <h3 className="font-display font-semibold text-slate-900 text-base pt-2">15. Contact Information</h3>
      <p>Questions regarding this Shipping Policy may be directed to:</p>
      <p>True Impact Peptides<br />Email: info@trueimpactpeptides.com</p>

      <p className="pt-4 italic text-slate-400">End of Shipping Policy</p>
    </LegalPage>
  );
}
